// FAQ answers: Markdown plus a few blocks of our own, rendered to safe HTML.
//
//   ## Section title            section heading (listed in "On this page")
//   ### Smaller heading
//   **bold**, *italic*, [link](https://…), - lists, 1. lists, > quote, | tables |
//   [yes] Yes  /  [no] No  /  [maybe] Not always      a coloured short answer
//   :::note … :::               highlighted warning box
//   :::formula … :::            centred line, e.g. A + B + C  or  A ≠ B
//   :::steps … :::              one card per line: Title | description   (start a line with * to highlight it)
//   :::compare … :::            ### Title | subtitle, then a list; two of these side by side
//   :::qa … :::                 ### Question, then its answer; shown as small expandable questions
//
// Raw HTML typed into an answer is shown as text, and the result goes through DOMPurify.
import { Marked } from 'marked';
import DOMPurify from 'dompurify';
import { PARTNER_HOSTS } from '@/helpers/partners';

const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

function makeMarked(prefix, toc) {
    const md = new Marked({ gfm: true, breaks: true });
    md.use({
        renderer: {
            html(token) { return esc(token.text || token.raw || ''); },   // no raw HTML from answers
            heading(token) {
                const inner = this.parser.parseInline(token.tokens);
                if (token.depth <= 2) {
                    const id = `${prefix}-s${toc.length + 1}`;
                    toc.push({ id, title: token.text.replace(/[*_`]/g, '') });
                    return `<h2 id="${id}">${inner}</h2>`;
                }
                return `<h3>${inner}</h3>`;
            },
        },
    });
    return md;
}

const CHIP = /^\[(yes|no|maybe)\]\s+(.+)$/i;

export function renderFaq(text, prefix = 'faq') {
    const toc = [];
    const md = makeMarked(prefix, toc);
    const inline = (s) => md.parseInline(String(s || ''));
    const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
    const out = [];
    let buf = [];
    const flush = () => { if (buf.length) { out.push(md.parse(buf.join('\n'))); buf = []; } };

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const open = line.trim().match(/^:::(note|formula|steps|compare|qa)\s*$/i);
        if (open) {
            flush();
            const kind = open[1].toLowerCase();
            const inner = [];
            i++;
            while (i < lines.length && lines[i].trim() !== ':::') inner.push(lines[i++]);
            out.push(block(kind, inner, md, inline));
            continue;
        }
        const chip = line.trim().match(CHIP);
        if (chip) {
            flush();
            out.push(`<p class="faq-chip-row"><span class="faq-chip is-${chip[1].toLowerCase()}">${inline(chip[2])}</span></p>`);
            continue;
        }
        buf.push(line);
    }
    flush();

    const clean = DOMPurify.sanitize(out.join('\n'), { USE_PROFILES: { html: true }, ADD_TAGS: ['details', 'summary'], ADD_ATTR: ['target', 'rel'], FORBID_ATTR: ['style'] });
    // finishing touches that are easier on the DOM
    const box = document.createElement('div');
    box.innerHTML = clean;
    box.querySelectorAll('a[href]').forEach((a) => {
        const href = a.getAttribute('href');
        if (/^https?:\/\//i.test(href)) {
            let host = ''; try { host = new URL(href).hostname.replace(/^www\./, ''); } catch (e) { host = ''; }
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', PARTNER_HOSTS.some((h) => host === h || host.endsWith('.' + h)) ? 'sponsored noopener' : 'noopener noreferrer');
        }
    });
    box.querySelectorAll('table').forEach((t) => {
        if (t.parentElement?.classList.contains('faq-table')) return;
        const wrap = document.createElement('div'); wrap.className = 'faq-table';
        t.parentNode.insertBefore(wrap, t); wrap.appendChild(t);
    });
    box.querySelectorAll('ul').forEach((ul) => {   // many short items: two columns
        const items = [...ul.children];
        if (items.length >= 6 && items.every((li) => li.textContent.trim().length <= 38)) ul.classList.add('is-cols');
    });
    const plain = box.textContent.replace(/\s+/g, ' ').trim();
    const words = plain ? plain.split(' ').length : 0;
    return { html: box.innerHTML, toc, words, plain };
}

function block(kind, inner, md, inline) {
    const body = inner.join('\n');
    if (kind === 'note') return `<div class="faq-note">${md.parse(body)}</div>`;
    if (kind === 'formula') {
        const html = inline(inner.filter((l) => l.trim()).join(' ')).replace(/\s(\+|≠|=)\s/g, ' <i>$1</i> ');
        return `<div class="faq-formula">${html}</div>`;
    }
    if (kind === 'steps') {
        const rows = inner.map((l) => l.trim()).filter(Boolean);
        return `<div class="faq-steps">${rows.map((row, n) => {
            const key = row.startsWith('*');
            const [title, ...rest] = row.replace(/^\*\s*/, '').split('|');
            return `<div class="faq-step${key ? ' is-key' : ''}"><span class="faq-step__n">${n + 1}</span><b>${inline(title.trim())}</b><span>${inline(rest.join('|').trim())}</span></div>`;
        }).join('')}</div>`;
    }
    if (kind === 'compare') {
        const cols = [];
        for (const l of inner) {
            const h = l.match(/^###\s+(.+)$/);
            if (h) { const [title, sub] = h[1].split('|'); cols.push({ title: title.trim(), sub: (sub || '').trim(), lines: [] }); }
            else if (cols.length) cols[cols.length - 1].lines.push(l);
        }
        return `<div class="faq-compare">${cols.map((c, n) => `<div class="faq-compare__col is-${n === 0 ? 'a' : 'b'}"><h4>${inline(c.title)}</h4>${c.sub ? `<small>${inline(c.sub)}</small>` : ''}${md.parse(c.lines.join('\n'))}</div>`).join('')}</div>`;
    }
    if (kind === 'qa') {
        const qs = [];
        for (const l of inner) {
            const h = l.match(/^###\s+(.+)$/);
            if (h) qs.push({ q: h[1].trim(), a: [] });
            else if (qs.length) qs[qs.length - 1].a.push(l);
        }
        return `<div class="faq-qa">${qs.map((x) => `<details><summary>${inline(x.q)}</summary><div>${md.parse(x.a.join('\n').trim())}</div></details>`).join('')}</div>`;
    }
    return md.parse(body);
}

// "Guide" badge and reading time for long answers
export const isGuide = (words) => words >= 600;
export const readMinutes = (words) => Math.max(1, Math.round(words / 200));
// an answer that is only a link is shown as a quick link at the top of the page, not as a question
export const quickLinkOf = (faq) => { const a = String(faq?.answer || '').trim(); return /^https?:\/\/\S+$/i.test(a) ? a : null; };
